const INACTIVITY_MS = 5_000;

/**
 * Persistent fullscreen control for browser-player session surfaces.
 *
 * Renderers replace their root content when payloads change, so this object
 * owns visibility state and document listeners independently of the button
 * node. Recreating the node therefore never flashes or restarts inactivity.
 */
export class FullscreenControl {
  private button: HTMLButtonElement | null = null;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private visible = true;
  private focused = false;

  private readonly activityHandler = () => this.reveal();
  private readonly fullscreenChangeHandler = () => {
    this.syncButton();
    this.reveal();
  };

  constructor(private readonly root: HTMLElement) {
    document.addEventListener('pointermove', this.activityHandler, { passive: true });
    document.addEventListener('pointerdown', this.activityHandler, { passive: true });
    document.addEventListener('touchstart', this.activityHandler, { passive: true });
    document.addEventListener('keydown', this.activityHandler);
    document.addEventListener('fullscreenchange', this.fullscreenChangeHandler);
    this.scheduleHide();
  }

  dispose(): void {
    this.clearTimer();
    document.removeEventListener('pointermove', this.activityHandler);
    document.removeEventListener('pointerdown', this.activityHandler);
    document.removeEventListener('touchstart', this.activityHandler);
    document.removeEventListener('keydown', this.activityHandler);
    document.removeEventListener('fullscreenchange', this.fullscreenChangeHandler);
    this.button = null;
  }

  render(): void {
    const lostInteractionHold = this.focused;
    // The owning renderer may have removed the previously focused button with
    // root.innerHTML. That DOM state cannot survive replacement.
    this.focused = false;
    if (lostInteractionHold) this.scheduleHide();
    this.button = null;
    if (typeof document.documentElement.requestFullscreen !== 'function') return;

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'ag-session-fullscreen-button';
    button.addEventListener('mouseenter', () => {
      this.reveal();
    });
    button.addEventListener('mouseleave', () => {
      this.scheduleHide();
    });
    button.addEventListener('focus', () => {
      this.focused = true;
      this.reveal();
    });
    button.addEventListener('blur', () => {
      this.focused = false;
      this.scheduleHide();
    });
    button.addEventListener('click', (event) => {
      const pointerActivated = event.detail !== 0;
      if (pointerActivated) {
        this.focused = false;
        button.blur();
      }
      this.reveal();
      const action = document.fullscreenElement
        ? document.exitFullscreen()
        : document.documentElement.requestFullscreen();
      if (action && typeof action.catch === 'function') void action.catch(() => {});
    });
    this.root.appendChild(button);
    this.button = button;
    this.syncButton();
    this.syncVisibility();
    this.syncAnnouncementClearance();
  }

  private reveal(): void {
    this.visible = true;
    this.syncVisibility();
    this.scheduleHide();
  }

  private scheduleHide(): void {
    this.clearTimer();
    if (this.focused) return;
    this.timer = setTimeout(() => {
      this.timer = null;
      if (this.focused) return;
      this.visible = false;
      this.syncVisibility();
    }, INACTIVITY_MS);
  }

  private clearTimer(): void {
    if (this.timer !== null) clearTimeout(this.timer);
    this.timer = null;
  }

  private syncVisibility(): void {
    this.button?.classList.toggle('ag-session-fullscreen-button--inactive', !this.visible);
  }

  private syncAnnouncementClearance(): void {
    const hasLowerThird = this.root.querySelector(
      '.ag-session__announcement, .ag-scene-announcements, .ag-scene-announcement-board'
    ) !== null;
    this.button?.classList.toggle('ag-session-fullscreen-button--lifted', hasLowerThird);
  }

  private syncButton(): void {
    if (!this.button) return;
    const active = Boolean(document.fullscreenElement);
    const label = active ? 'Exit fullscreen' : 'Enter fullscreen';
    this.button.setAttribute('aria-label', label);
    this.button.title = label;
    this.button.innerHTML = fullscreenIconSvg(active);
  }
}

function fullscreenIconSvg(active: boolean): string {
  const path = active
    ? 'M9 3v6H3M15 3v6h6M9 21v-6H3M15 21v-6h6'
    : 'M8 3H3v5M16 3h5v5M8 21H3v-5M16 21h5v-5';
  return `<svg aria-hidden="true" viewBox="0 0 24 24" width="30" height="30" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="${path}"></path></svg>`;
}
